import imagesLoaded from 'imagesloaded'
import { GetServerSideProps } from 'next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import Head from 'next/head'
// eslint-disable-next-line import/no-named-as-default
import React, { useEffect, useRef, useState } from 'react'
import LiveChat from 'react-livechat'
import * as Sentry from '@sentry/browser'
import config from '../../../config'
import { Layout } from '../../common/Layout/Layout'
import { Meta } from '../../common/Meta'
import { Header, HeaderButtons } from '../../components/Header/Header'
import RoomDesktop from '../../components/Pages/Room/RoomDesktop/RoomDesktop'
import { StageCurtains } from '../../components/RoomExperience'
import { LISAEventResolved } from '../../types/lisa'
import { PageError } from '../../types/pageError'
import { Expo } from '../../types/viewModels'
import { fetchEventsForPage, fetchExpo } from '../../utils/cms'
import { useEvents } from '../../utils/hooks/useEvents'
import { useLiveEvents } from '../../utils/hooks/useLiveEvents'
import { checkAuth } from '../../utils/auth'

const BREAKPOINT = 550

export const getServerSideProps: GetServerSideProps = async (context) => {
  const auth = await checkAuth(context.req.headers.cookie)

  // if (!auth.user && config.LOGIN_TYPE !== 'NONE') {
  //   return {
  //     redirect: {
  //       destination: '/',
  //       permanent: false,
  //     },
  //   }
  // }

  const { locale } = context
  // Fetch data
  let expo: Array<Expo> = []
  let events: LISAEventResolved[] = []

  try {
    [expo, events] = await Promise.all([fetchExpo(), fetchEventsForPage()])
  }
  catch (error) {
    //Send the error the sentry without disrupting the users experience
    Sentry.setContext('expo', { 'expo' : expo })
    Sentry.setContext('events', { 'events' : events })
    Sentry.setContext('user', { 'user' : auth.user })
      
    Sentry.captureException(new Error('Error in server side props room/[slug].tsx'))
  }

  const slug = context.params !== undefined && context.params.slug !== undefined ? context.params.slug.toString() : ''
  let room : Expo | null = null
  // If the room has a uid(pretty url) we use that
  if (expo) {
    [room] = expo.filter((x) => x.uid === slug)
  }

  expo = expo.filter((item) => room && item.overviewRoomName === room.overviewRoomName && !item.outbound)

  console.log('expo', expo)
  // No rooms found.
  if (!room) {
    const pageError: PageError = { error: { statusText: 'not found' } }
    return {
      props: {
        pageError,
      },
    }
  }

  const showroomIndex = expo.findIndex((document) => document.uid === slug)
  const previousRoom = expo[showroomIndex - 1]
  const nextRoom = expo[showroomIndex + 1]
  const hrefRoomPrevious = previousRoom && previousRoom.company ? `/room/${previousRoom.uid}` : ''
  const hrefRoomNext = nextRoom && nextRoom.company ? `/room/${nextRoom.uid}` : ''

  return {
    props: {
      room,
      hrefRoomPrevious,
      hrefRoomNext,
      events,
      user: auth.user,
      ...await serverSideTranslations(locale, ['common']),
    },
  }
}

// export const getStaticPaths:GetStaticPaths<{ slug: string }> = async () => {
//   const allRooms = await fetchExpo()
//   const pathsEN = allRooms.map(({ uid }) => ({ params: { slug: uid }, locale: Locale.en }))
//   const pathsIS = allRooms.map(({ uid }) => ({ params: { slug: uid }, locale: Locale.is }))
//   const paths = [...pathsEN, ...pathsIS]
//   return {
//     paths,
//     fallback: false,
//   }
// }

enum ViewState {
  Desktop,
  Mobile,
}

interface RoomProps {
  room: Expo
  hrefRoomPrevious?: string
  events: LISAEventResolved[]
  hrefRoomNext?: string
}
const Room: React.FC<RoomProps> = ({
  room, 
  hrefRoomPrevious, 
  hrefRoomNext, 
  events: eventsProp,
}) => {
  const events = useEvents(eventsProp)
  const liveEvents = useLiveEvents(events)

  // We have two different visuals for desktop and mobile.
  const [width, setWidth] = useState<number | null>(null)
  useEffect(() => {
    const handleResizeWindow = () => {
      setWidth(window.innerWidth)
    }
    window.addEventListener('resize', handleResizeWindow)
    handleResizeWindow()

    return () => {
      // unsubscribe "onComponentDestroy"
      window.removeEventListener('resize', handleResizeWindow)
    }
  }, [])

  // We want to prerender the desktop version first.
  let viewState = ViewState.Desktop
  if (width === null) {
    viewState = ViewState.Desktop
  }
  else if (width < BREAKPOINT) {
    viewState = ViewState.Mobile
  }
  else {
    viewState = ViewState.Desktop
  }

  const [loading, setLoading] = useState(true)

  useEffect(() => {
    setLoading(false)
  }, [])

  const pageButtons: HeaderButtons = [
    { key: 1, variant: 'menu' },
    { key: 2, variant: 'home' },
    // { key: 3, variant: 'contact' },
  ]

  // Images can take a long time loading or a short while
  // The rooms look ugly when loading so we wait.
  // When they load fast a minimum of 800ms loader is displayed
  // because that looks better
  const refDesktop = useRef<HTMLDivElement>(null)
  const [roomReady, setRoomReady] = useState(false)
  useEffect(() => {
    // vanilla JS
    if (!refDesktop.current) {
      return
    }

    let minimumTimeLapsed = false
    let allImagesLoaded = false
    imagesLoaded(refDesktop.current, { background: '.room-wallpaper' }, () => {
      allImagesLoaded = true
      if (minimumTimeLapsed) {
        setRoomReady(true)
      }
    })

    setTimeout(() => {
      minimumTimeLapsed = true
      if (allImagesLoaded) {
        setRoomReady(true)
      }
    }, 800)
  }, [])

  return (
    <Layout>
      <Head>
        <title>{`${config.PROJECT_NAME_SEO} | Expo - ${room.company}`}</title>
      </Head>
      <Meta />
      <Header buttons={pageButtons} liveEvents={liveEvents} />

      <StageCurtains show={!roomReady} color={room.brandColorhex ? room.brandColorhex : '#fff'} />

      <RoomDesktop
        contentLoaded={roomReady}
        room={room}
        ref={refDesktop}
        hrefRoomNext={hrefRoomNext}
        hrefRoomPrevious={hrefRoomPrevious}
        mobileView={viewState === ViewState.Mobile}
      />

      {!loading && room.livechatid && <LiveChat license={13080600} group={parseInt(room.livechatid)} chatBetweenGroups={false} /> }

      {/* {viewState === ViewState.Desktop && (
        <RoomDesktop
          contentLoaded={roomReady}
          room={room}
          ref={refDesktop}
          hrefRoomNext={hrefRoomNext}
          hrefRoomPrevious={hrefRoomPrevious}
          color={room.brandColorhex}
        />
      )}
      {viewState === ViewState.Mobile && <RoomMobile room={room} />} */}
    </Layout>
  )
}

export default Room
